import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { FormattedMessage, useIntl } from 'react-intl';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEMAND_MONITORING_ROOT } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandHome';

interface DemandCategoryTableRow extends DemandTableRow {
  category: string;
}

export function CategoryTable({ merchantData, filters, localFilterFunction }: DemandTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const { getMeasureByMetric, getAttributes } = useModelService();
  const [prdCategory] = getAttributes(['prdCategory']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const columnHelper = createColumnHelper<DemandCategoryTableRow>();
  const defaultColumns = buildStandardColumns(ordQty, intl);

  const buildColumns = () => [
    columnHelper.accessor('category', {
      id: 'category',
      header: () => prdCategory.name,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: info => (
        <div className="flex gap-2 items-center">
          {findIconForCategory(info.getValue(), 24)}
          <FormattedMessage id={`product.categories.${info.getValue()}`} />
        </div>
      ),
    }),
  ];

  const cols: ColumnDef<DemandCategoryTableRow>[] = (buildColumns() as ColumnDef<ProductDetailsTableRow>[]).concat(
    defaultColumns as ColumnDef<ProductDetailsTableRow>[]
  ) as ColumnDef<DemandCategoryTableRow>[];

  const { data } = useEntriesDemandReportData(prdCategory.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandCategoryTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        prdCategory,
        ordGmvVal,
        ordQty,
        entry => ({
          category: prdCategory.getRawValue(entry.attributes),
        }),
        setTableRows,
        localFilterFunction
      );
    }
  }, [data, merchantData, currentDataVersion, localFilterFunction]);

  const showDetails = (row: Row<DemandCategoryTableRow>) => {
    navigate(`${DEMAND_MONITORING_ROOT}/detail/${prdCategory.code}/${row.original.category}${location.search}`);
  };

  const defaultExportConfig = createCellExportOptions('Categories');

  return (
    <>
      <DataTableContextProvider>
        <DataTable
          onRowClick={showDetails}
          rows={tableRows}
          columns={cols}
          titleId="insights.category"
          defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
          exportConfigOptions={{
            ...defaultExportConfig,
            customCellValueExporters: {
              ...defaultExportConfig.customCellValueExporters,
              category: (cellValue, intl) => {
                return intl.formatMessage({ id: `product.categories.${cellValue as string}` });
              },
            },
          }}
        />
      </DataTableContextProvider>
    </>
  );
}
